import { useEffect, useState } from "react";
import { useOrganizationsList } from "../api/organization";

type UseSearchableOrganizationsOptions = {
  merchantType: string;
  status?: string;
  pageSize?: number;
  getLabel?: (item: { id: number; name: string }) => string;
};

export function useSearchableOrganizations({
  merchantType,
  status = "ACTIVE",
  pageSize = 10,
  getLabel = (item) => item.name,
}: UseSearchableOrganizationsOptions) {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [allOptions, setAllOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const { data, isLoading, isFetching } = useOrganizationsList({
    merchantType,
    status,
    page,
    size: pageSize,
    search: searchTerm || undefined,
  });

  useEffect(() => {
    setPage(0);
    setAllOptions([]);
    setHasMore(true);
  }, [searchTerm, merchantType]);

  useEffect(() => {
    if (!data?.data?.content) return;

    const newOptions = data.data.content.map((item: { id: number; name: string }) => ({
      label: getLabel(item),
      value: String(item.id),
    }));

    if (page === 0) {
      setAllOptions(newOptions);
    } else {
      setAllOptions((prev) => [...prev, ...newOptions]);
    }

    setHasMore(!data.data.last);
  }, [data, page, getLabel]);

  const handleSearch = (search: string) => {
    setSearchTerm(search);
  };

  const handleLoadMore = () => {
    if (hasMore && !isFetching && !isLoadingMore) {
      setIsLoadingMore(true);
      setPage((prev) => prev + 1);
      setIsLoadingMore(false);
    }
  };

  return {
    options: allOptions,
    isLoading: isLoading || isFetching,
    isLoadingMore,
    hasMore,
    onSearch: handleSearch,
    onLoadMore: handleLoadMore,
  };
}
